import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import MiniGraph from '../lineGraph/mini';

// Mirrors components/race/weatherSummary.js, but loads weather scoped to a
// convention's location + date range (past → WeatherHistory, future → yr.no
// forecast — handled server-side in /api/yr/convention-summary).
@observer
class ConventionWeatherSummary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            weatherSummary: null,
            loading: true,
            error: null,
            containerWidth: 350,
        };
        this.containerRef = null;
    }

    async loadWeatherSummary() {
        const { conventionId, stores } = this.props;
        const { gpsPointStore } = stores;
        try {
            this.setState({ loading: true, error: null });
            const result = await gpsPointStore.getConventionWeatherSummary({ conventionId });
            if (result && result.status === 200 && result.data) {
                this.setState({ weatherSummary: result.data, loading: false }, () => {
                    setTimeout(() => this.measureContainer(), 0);
                });
            } else {
                this.setState({ weatherSummary: null, loading: false });
            }
        } catch (err) {
            this.setState({ error: err.message, loading: false });
        }
    }

    componentDidMount() {
        this.loadWeatherSummary();
        this.measureContainer();
        window.addEventListener('resize', this.measureContainer);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.measureContainer);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.conventionId !== this.props.conventionId) {
            this.loadWeatherSummary();
        }
    }

    measureContainer = () => {
        if (this.containerRef) {
            const width = this.containerRef.offsetWidth - 20;
            this.setState({ containerWidth: Math.max(200, width) });
        }
    }

    downsampleData(records, targetPoints = 30) {
        if (records.length <= targetPoints) return records;
        const groupSize = Math.ceil(records.length / targetPoints);
        const downsampled = [];
        for (let i = 0; i < records.length; i += groupSize) {
            const group = records.slice(i, Math.min(i + groupSize, records.length));
            const avgTemp = group.reduce((sum, r) => sum + (r.temperature || 0), 0) / group.length;
            const avgWind = group.reduce((sum, r) => sum + (r.windSpeed || 0), 0) / group.length;
            const avgPrecip = group.reduce((sum, r) => sum + (r.precipitation || 0), 0) / group.length;
            const middle = group[Math.floor(group.length / 2)];
            downsampled.push({
                timeWindow: middle.timeWindow,
                temperature: avgTemp,
                windSpeed: avgWind,
                precipitation: avgPrecip,
                symbolCode: middle.symbolCode,
            });
        }
        return downsampled;
    }

    prepareGraphData() {
        const { weatherSummary } = this.state;
        if (!weatherSummary?.records?.length) {
            return { tempData: [], windData: [], precipData: [], symbolMarkers: [] };
        }
        const records = this.downsampleData(weatherSummary.records, 30);
        const tempData = records.map((r, i) => ({ epoch: new Date(r.timeWindow).getTime(), x: i, y: r.temperature }));
        const windData = records.map((r, i) => ({ epoch: new Date(r.timeWindow).getTime(), x: i, y: r.windSpeed }));
        const precipData = records.map((r, i) => ({ epoch: new Date(r.timeWindow).getTime(), x: i, y: r.precipitation || 0 }));
        const symbolInterval = Math.max(1, Math.floor(records.length / 6));
        const symbolMarkers = [];
        for (let i = 0; i < records.length; i += symbolInterval) {
            const r = records[i];
            symbolMarkers.push({ index: i, symbolCode: r.symbolCode, time: r.timeWindow, temp: r.temperature });
        }
        const lastIdx = records.length - 1;
        if (symbolMarkers.length === 0 || symbolMarkers[symbolMarkers.length - 1].index !== lastIdx) {
            const r = records[lastIdx];
            symbolMarkers.push({ index: lastIdx, symbolCode: r.symbolCode, time: r.timeWindow, temp: r.temperature });
        }
        return { tempData, windData, precipData, symbolMarkers };
    }

    getWeatherIcon(symbolCode) {
        if (!symbolCode) return 'fa-solid fa-cloud';
        const iconMap = {
            clearsky: 'fa-solid fa-sun',
            fair: 'fa-solid fa-cloud-sun',
            partlycloudy: 'fa-solid fa-cloud-sun',
            cloudy: 'fa-solid fa-cloud',
            rain: 'fa-solid fa-cloud-rain',
            lightrain: 'fa-solid fa-cloud-rain',
            heavyrain: 'fa-solid fa-cloud-showers-heavy',
            snow: 'fa-solid fa-snowflake',
            lightsnow: 'fa-solid fa-snowflake',
            heavysnow: 'fa-solid fa-snowflake',
            sleet: 'fa-solid fa-cloud-meatball',
            fog: 'fa-solid fa-smog',
            thunder: 'fa-solid fa-bolt',
        };
        const base = symbolCode.replace(/_day|_night/g, '');
        return iconMap[base] || 'fa-solid fa-cloud';
    }

    getWeatherIconColor(symbolCode) {
        if (!symbolCode) return '#9ca3af';
        const colorMap = {
            clearsky: '#fbbf24',
            fair: '#fbbf24',
            partlycloudy: '#9ca3af',
            cloudy: '#9ca3af',
            rain: '#3b82f6',
            lightrain: '#60a5fa',
            heavyrain: '#1d4ed8',
            snow: '#93c5fd',
            lightsnow: '#bfdbfe',
            heavysnow: '#60a5fa',
            sleet: '#6b7280',
            fog: '#9ca3af',
            thunder: '#fbbf24',
        };
        const base = symbolCode.replace(/_day|_night/g, '');
        return colorMap[base] || '#9ca3af';
    }

    formatTemp(temp) { return temp == null ? '-' : `${Math.round(temp)}°`; }
    formatWind(wind) { return wind == null ? '-' : `${Math.round(wind)} m/s`; }

    render() {
        const { weatherSummary, loading, error, containerWidth } = this.state;
        const { stores } = this.props;
        const { userStore } = stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (!loading && !weatherSummary) return null;
        if (error) return null;

        if (loading) {
            return (
                <div class='w-100 d-flex flex-column mt-2 mb-1'>
                    <div class={`d-flex flex-row align-items-center justify-content-center mx-3 px-3 py-2 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        <small class='text-muted'>
                            <i class='fa-solid fa-spinner fa-spin me-2' />
                            <Text id='convention.loading-weather'>Loading weather...</Text>
                        </small>
                    </div>
                </div>
            );
        }

        const { firstRecord, lastRecord, minTemp, maxTemp, minWind, maxWind, totalPrecipitation, mostCommonSymbol } = weatherSummary;
        const { tempData, windData, precipData, symbolMarkers } = this.prepareGraphData();
        const hasGraphData = tempData.length > 1;

        return (
            <div class='w-100 d-flex flex-column mt-2 mb-1'>
                <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <div class='d-flex flex-column flex-md-row align-items-center justify-content-md-between px-3 py-2'>
                        <div class='d-flex flex-row align-items-center mb-2 mb-md-0'>
                            <i class={`${this.getWeatherIcon(mostCommonSymbol)} text-primary me-2`} style='font-size: 1.5em;' />
                            <span class='text-muted font-weight-light'>
                                <Text id='convention.weather-summary'>Weather</Text>
                            </span>
                        </div>

                        <div class='d-flex flex-row flex-wrap align-items-center justify-content-center mb-2 mb-md-0'>
                            {firstRecord && (
                                <div class='d-flex flex-column align-items-center px-2'>
                                    <small class='text-muted' style='font-size: 0.7em;'>
                                        {util.formatDate(firstRecord.timeWindow, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}
                                    </small>
                                    <div class='d-flex flex-row align-items-center'>
                                        <i class={`${this.getWeatherIcon(firstRecord.symbolCode)} me-1`} />
                                        <span class='font-weight-bold'>{this.formatTemp(firstRecord.temperature)}</span>
                                    </div>
                                    <small class='text-muted'>
                                        <i class='fa-solid fa-wind me-1' style='font-size: 0.8em;' />
                                        {this.formatWind(firstRecord.windSpeed)}
                                    </small>
                                </div>
                            )}
                            {firstRecord && lastRecord && firstRecord.id !== lastRecord.id && (
                                <div class='px-2'>
                                    <i class='fa-solid fa-arrow-right text-muted' />
                                </div>
                            )}
                            {lastRecord && firstRecord && firstRecord.id !== lastRecord.id && (
                                <div class='d-flex flex-column align-items-center px-2'>
                                    <small class='text-muted' style='font-size: 0.7em;'>
                                        {util.formatDate(lastRecord.timeWindow, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}
                                    </small>
                                    <div class='d-flex flex-row align-items-center'>
                                        <i class={`${this.getWeatherIcon(lastRecord.symbolCode)} me-1`} />
                                        <span class='font-weight-bold'>{this.formatTemp(lastRecord.temperature)}</span>
                                    </div>
                                    <small class='text-muted'>
                                        <i class='fa-solid fa-wind me-1' style='font-size: 0.8em;' />
                                        {this.formatWind(lastRecord.windSpeed)}
                                    </small>
                                </div>
                            )}
                        </div>

                        <div class='d-flex flex-column align-items-center align-items-md-end'>
                            <div class='d-flex flex-row align-items-center'>
                                <small class='text-muted me-1'><i class='fa-solid fa-thermometer-half' /></small>
                                <small class='text-primary'>{this.formatTemp(minTemp)}</small>
                                <small class='text-muted mx-1'>-</small>
                                <small class='text-danger'>{this.formatTemp(maxTemp)}</small>
                            </div>
                            <div class='d-flex flex-row align-items-center'>
                                <small class='text-muted me-1'><i class='fa-solid fa-wind' /></small>
                                <small>{this.formatWind(minWind)}</small>
                                <small class='text-muted mx-1'>-</small>
                                <small>{this.formatWind(maxWind)}</small>
                            </div>
                            {totalPrecipitation > 0 && (
                                <div class='d-flex flex-row align-items-center'>
                                    <small class='text-muted me-1'><i class='fa-solid fa-cloud-rain' /></small>
                                    <small class='text-info'>{totalPrecipitation.toFixed(1)} mm</small>
                                </div>
                            )}
                        </div>
                    </div>

                    {hasGraphData && (
                        <div class='d-flex flex-column px-2 pb-2' ref={el => { this.containerRef = el; }}>
                            <MiniGraph
                                stores={stores}
                                dataLeft={[tempData]}
                                showYTicksLeft={1}
                                yTicksLeftVal={val => `${Math.round(val)}°`}
                                smoothLeft={[true]}
                                gradientFillLeft={['#3b82f6']}
                                inputStyleLeft={'stroke: #3b82f6; stroke-width: 3px;'}
                                dataRight={[precipData, windData]}
                                displayTypeRight={['bar', 'line']}
                                showYTicksRight={1}
                                yTicksRightVal={val => `${Math.round(val)}`}
                                yTicksRightWidth='30px'
                                inputStyleRight={['fill: #60a5fa; opacity: 0.6;', 'stroke: #14b8a6; stroke-width: 2px;']}
                                barWidth={8}
                                barOpacity={0.6}
                                width={containerWidth}
                                height={100}
                                paddingLeft={35}
                                paddingRight={30}
                                paddingTop={10}
                                paddingBottom={20}
                                showXTicks={1}
                                xTicksVal={val => util.formatDate(val, { hour: '2-digit', minute: '2-digit', hour12: false })}
                                xTicksCnt={4}
                                gradientOpacity={0.15}
                            />
                            <div class='d-flex flex-row justify-content-end align-items-center px-2' style='margin-top: -4px;'>
                                <small class='me-3' style='font-size: 0.7em;'>
                                    <span style='display: inline-block; width: 12px; height: 8px; background: #60a5fa; opacity: 0.7; margin-right: 4px; vertical-align: middle;' />
                                    <span class='text-muted'>mm</span>
                                </small>
                                <small style='font-size: 0.7em;'>
                                    <span style='display: inline-block; width: 12px; height: 2px; background: #14b8a6; margin-right: 4px; vertical-align: middle;' />
                                    <span class='text-muted'>m/s</span>
                                </small>
                            </div>
                            <div class='position-relative' style='height: 24px; margin-left: 35px; margin-right: 55px;'>
                                {symbolMarkers.map((marker, idx) => {
                                    const maxIndex = tempData.length - 1;
                                    const percentage = maxIndex > 0 ? (marker.index / maxIndex) * 100 : 0;
                                    return (
                                        <i
                                            key={idx}
                                            class={this.getWeatherIcon(marker.symbolCode)}
                                            style={`position: absolute; left: ${percentage}%; transform: translateX(-50%); font-size: 1em; color: ${this.getWeatherIconColor(marker.symbolCode)};`}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default ConventionWeatherSummary;
